import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Container, ThreeColumnGrid } from '../shared.styled';
import { AssignedCareTeamPeople } from './components/AssignedCareTeamPeople';
import { PreferredLocation } from './components/PreferredLocation';
import { Header, Section } from './styled';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import { useHomeMarket } from 'hooks/useHomeMarket';
import theme from 'kb-shared/theme';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { pageUrl } from 'utilities/pageUrl';

import { CARE_TEAM_QUERY, CareTeamResponse } from './CareTeam.graphql';
import { hasRequiredLocationData } from './CareTeam.utils';

export const CareTeam = () => {
  const { name: homeMarketName } = useHomeMarket();
  const { data, error } = useQuery<CareTeamResponse>(CARE_TEAM_QUERY);
  const shouldRenderPharmacyLocation = hasRequiredLocationData(data?.patientPharmacy);
  const shouldRenderLabLocation = hasRequiredLocationData(data?.patientLabCenter);
  const shouldRenderRadiologyLocation = hasRequiredLocationData(data?.patientRadiologyCenter);

  useEffect(() => {
    if (error) {
      BugTracker.notify(error, 'Failed to fetch care team information');
    }
  }, [error]);

  return (
    <Container>
      <Header>
        <Heading tag="span" styledAs="h4">
          Your Care Team
        </Heading>
        <Text size="sm">
          Your team of physicians, nurse practitioners, physician assistants, endocrinologists, care
          coordinators, financial navigators, patient experience navigators, and cloud nurses are
          dedicated to caring for you.
        </Text>
      </Header>

      {homeMarketName && (
        <Header>
          <Heading tag="span" styledAs="h4">
            Your Home Market: {homeMarketName}
          </Heading>

          <Text tag="p" size="sm">
            Need to change the home market?{' '}
            <Text tag="span" color={theme.colors.blue.darkBlue}>
              <Link data-testid="change-home-market-link" to={pageUrl.messages()}>
                Send us a message!
              </Link>
            </Text>
          </Text>
        </Header>
      )}

      {data && (
        <>
          <ThreeColumnGrid>
            <AssignedCareTeamPeople careTeam={data.careTeam} />
          </ThreeColumnGrid>

          {(shouldRenderLabLocation ||
            shouldRenderPharmacyLocation ||
            shouldRenderRadiologyLocation) && (
            <Section>
              <Heading tag="span" styledAs="h4">
                Preferred Locations
              </Heading>
              <ThreeColumnGrid>
                {shouldRenderPharmacyLocation && (
                  <PreferredLocation type="Preferred pharmacy" location={data.patientPharmacy} />
                )}
                {shouldRenderLabLocation && (
                  <PreferredLocation type="Preferred lab center" location={data.patientLabCenter} />
                )}
                {shouldRenderRadiologyLocation && (
                  <PreferredLocation
                    type="Preferred radiology center"
                    location={data.patientRadiologyCenter}
                  />
                )}
              </ThreeColumnGrid>
            </Section>
          )}
        </>
      )}
    </Container>
  );
};
